import { onError, post, put } from 'src/api/api';
import { clientConfig } from 'src/api/axios/config';
import { useAuth } from 'src/contexts/AuthContext';
import { useAppNavigation } from 'src/routes';

import { useMutation } from '@tanstack/react-query';

const URI = 'token';

export interface LoginData {
  username: string;
  password: string;
}
export interface Auth0TokenData {
  auth0Token: string;
}

export interface RefreshTokenData {
  client: string;
  refreshToken: string;
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  status: number;
}

const postToken = async (data: LoginData | Auth0TokenData | RefreshTokenData) => {
  const response = await post({ uri: URI, data });
  return response.data;
};

const setInitialClient = async (token: string) => {
  try {
    const decodedToken = await JSON.parse(atob(token.split('.')[1]));
    const scopes = await JSON.parse(decodedToken.scopes);
    const client = scopes[0].client;
    clientConfig.setClient(client);
  } catch (error) {
    console.error('Error setting initial client', error);
  }
};

const usePostToken = () => {
  const { login } = useAuth();

  return useMutation({
    mutationFn: (data: LoginData | Auth0TokenData | RefreshTokenData) => postToken(data),
    onSuccess: (data: LoginResponse) => {
      const { token, refreshToken } = data;
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);
      setInitialClient(token);
      login(token);
    },
    onError,
  });
};

const postInvitationCode = async (invitationCode: string) => {
  const data = {
    invitationCode,
    client: clientConfig.getClient()?.length > 0 ? clientConfig.getClient() : 'system',
  };
  const response = await post({ uri: URI, data });
  return response.data;
};

const usePostInvitationCode = () => {
  return useMutation({
    mutationFn: (invitationCode: string) => postInvitationCode(invitationCode),
    // A rejected invitation code can come back as a tokenless (or absent) body. This handler runs
    // before any per-call onSuccess, so it has to do its own validation - otherwise it destructures
    // undefined, or writes a bogus token into auth state that no later check can take back.
    onSuccess: (data) => {
      const token = data?.token;
      if (!token) {
        return;
      }
      localStorage.setItem('token', token);
      setInitialClient(token);
    },
  });
};

const postResetPassword = async (token: string, password: string) => {
  if (!token) {
    throw new Error('No token provided');
  }
  const decodedToken = JSON.parse(atob(token.split('.')[1]));
  const username = decodedToken.sub;
  if (!username) {
    throw new Error('No username found in token');
  }

  const client = clientConfig.getClient()?.length > 0 ? clientConfig.getClient() : 'system';

  const response = await put({
    uri: `users/${username}/set-password`,
    data: {
      password,
      client,
    },
    headers: {
      Authorization: `Bearer ${token}`,
      Client: client,
    },
  });

  return response.data;
};

const usePostResetPassword = () => {
  const { navigateTo } = useAppNavigation();
  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) => postResetPassword(token, password),
    onSuccess: () => {
      navigateTo.login({ passwordResetSuccess: 'true' });
      localStorage.removeItem('token');
      localStorage.removeItem('client');
      setInitialClient('');
    },
    onError,
  });
};

export {
  postInvitationCode,
  postResetPassword,
  setInitialClient,
  usePostInvitationCode,
  usePostResetPassword,
  usePostToken,
};
